import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, CalendarOff, Clock } from "lucide-react";

const WD = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];

export default function WorkingHours() {
  const [hours, setHours] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [open, setOpen] = useState(false);
  const [ov, setOv] = useState({ date: "", is_closed: false, open: "", close: "", note: "", breaks: [] });

  const load = () => { api.get("/working-hours").then((r) => setHours(r.data || [])); api.get("/day-overrides").then((r) => setOverrides(r.data || [])); };
  useEffect(() => { load(); }, []);

  const update = (wd, patch) => setHours(hours.map((h) => (h.weekday === wd ? { ...h, ...patch } : h)));

  const addBreak = (wd) => update(wd, { breaks: [...(hours.find((h) => h.weekday === wd).breaks || []), { start: "12:00", end: "13:00", label: "Intervalo" }] });
  const updateBreak = (wd, i, patch) => { const h = hours.find((x) => x.weekday === wd); const b = [...h.breaks]; b[i] = { ...b[i], ...patch }; update(wd, { breaks: b }); };
  const removeBreak = (wd, i) => { const h = hours.find((x) => x.weekday === wd); update(wd, { breaks: h.breaks.filter((_, idx) => idx !== i) }); };

  const saveHours = async () => { try { await api.put("/working-hours", hours); toast.success("Horários salvos. A agenda foi atualizada."); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  const saveOverride = async () => {
    try {
      const payload = { ...ov, open: ov.open || null, close: ov.close || null };
      await api.put("/day-overrides", payload); toast.success("Exceção salva"); setOpen(false); setOv({ date: "", is_closed: false, open: "", close: "", note: "", breaks: [] }); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const removeOverride = async (d) => { try { await api.delete(`/day-overrides/${d}`); load(); } catch {} };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">Horários</h1><p className="text-zinc-400 mt-1">Controle total do funcionamento e disponibilidade.</p></div>
        <Button className="bg-gradient-to-r from-amber-400 to-yellow-600 text-black font-bold rounded-full" onClick={saveHours} data-testid="save-hours-btn">Salvar Horários</Button>
      </div>

      <div className="space-y-3 mb-10">
        {hours.map((h) => (
          <div key={h.weekday} className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5" data-testid={`weekday-${h.weekday}`}>
            <div className="flex flex-wrap items-center gap-4">
              <div className="w-28 font-semibold text-white flex items-center gap-2"><Clock className="w-4 h-4 text-amber-400" />{WD[h.weekday]}</div>
              <div className="flex items-center gap-2"><Switch checked={h.is_open} onCheckedChange={(v) => update(h.weekday, { is_open: v })} data-testid={`open-toggle-${h.weekday}`} /><span className="text-sm text-zinc-400">{h.is_open ? "Aberto" : "Fechado / Folga"}</span></div>
              {h.is_open && (
                <div className="flex items-center gap-2">
                  <Input type="time" value={h.open} onChange={(e) => update(h.weekday, { open: e.target.value })} className="bg-zinc-800 border-zinc-700 w-32" data-testid={`open-time-${h.weekday}`} />
                  <span className="text-zinc-500">até</span>
                  <Input type="time" value={h.close} onChange={(e) => update(h.weekday, { close: e.target.value })} className="bg-zinc-800 border-zinc-700 w-32" data-testid={`close-time-${h.weekday}`} />
                </div>
              )}
            </div>
            {h.is_open && (
              <div className="mt-4 pl-2 sm:pl-32 space-y-2">
                {(h.breaks || []).map((b, i) => (
                  <div key={i} className="flex items-center gap-2 flex-wrap">
                    <Input value={b.label} onChange={(e) => updateBreak(h.weekday, i, { label: e.target.value })} className="bg-zinc-800 border-zinc-700 w-32" placeholder="Intervalo" />
                    <Input type="time" value={b.start} onChange={(e) => updateBreak(h.weekday, i, { start: e.target.value })} className="bg-zinc-800 border-zinc-700 w-28" />
                    <span className="text-zinc-500">-</span>
                    <Input type="time" value={b.end} onChange={(e) => updateBreak(h.weekday, i, { end: e.target.value })} className="bg-zinc-800 border-zinc-700 w-28" />
                    <Button size="icon" variant="ghost" className="text-zinc-500 hover:text-red-400 h-8 w-8" onClick={() => removeBreak(h.weekday, i)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
                <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-400" onClick={() => addBreak(h.weekday)} data-testid={`add-break-${h.weekday}`}><Plus className="w-4 h-4 mr-1" /> Intervalo / Almoço</Button>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mb-4">
        <div><h2 className="font-heading text-xl font-bold text-white">Exceções por Data</h2><p className="text-zinc-400 text-sm">Folgas, bloqueios e horários especiais para dias específicos.</p></div>
        <Button variant="outline" className="border-amber-500/40 text-amber-100" onClick={() => setOpen(true)} data-testid="add-override-btn"><Plus className="w-4 h-4 mr-1" /> Exceção</Button>
      </div>
      {overrides.length === 0 ? (
        <div className="text-center py-10 bg-zinc-900 border border-zinc-800 rounded-2xl text-zinc-500">Nenhuma exceção cadastrada.</div>
      ) : (
        <div className="space-y-2">
          {overrides.map((o) => (
            <div key={o.date} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 flex items-center justify-between" data-testid={`override-${o.date}`}>
              <div className="flex items-center gap-3">
                <CalendarOff className={`w-5 h-5 ${o.is_closed ? "text-red-400" : "text-amber-400"}`} />
                <div>
                  <div className="text-white font-semibold">{new Date(o.date + "T00:00:00").toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })}</div>
                  <div className="text-sm text-zinc-400">{o.is_closed ? "Dia fechado / Folga" : `Horário especial: ${o.open || "-"} às ${o.close || "-"}`}{o.note ? ` • ${o.note}` : ""}</div>
                </div>
              </div>
              <Button size="icon" variant="ghost" className="text-zinc-500 hover:text-red-400" onClick={() => removeOverride(o.date)} data-testid={`delete-override-${o.date}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader><DialogTitle className="text-white">Nova Exceção de Data</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-zinc-300">Data</Label><Input type="date" value={ov.date} onChange={(e) => setOv({ ...ov, date: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="override-date" /></div>
            <div className="flex items-center gap-3"><Switch checked={ov.is_closed} onCheckedChange={(v) => setOv({ ...ov, is_closed: v })} data-testid="override-closed" /><Label className="text-zinc-300">Fechar o dia inteiro (folga)</Label></div>
            {!ov.is_closed && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-zinc-300">Abertura</Label><Input type="time" value={ov.open} onChange={(e) => setOv({ ...ov, open: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="override-open" /></div>
                <div><Label className="text-zinc-300">Fechamento</Label><Input type="time" value={ov.close} onChange={(e) => setOv({ ...ov, close: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="override-close" /></div>
              </div>
            )}
            <div><Label className="text-zinc-300">Observação</Label><Input value={ov.note} onChange={(e) => setOv({ ...ov, note: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="override-note" /></div>
          </div>
          <DialogFooter><Button onClick={saveOverride} disabled={!ov.date} className="bg-amber-400 text-black font-bold" data-testid="save-override-btn">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
